import { useState } from 'react';
import { Link } from 'react-router-dom';
import { getSupabaseClient } from '../../data/supabase-client';
import { useMyDayHistory } from '../../data/queries/use-my-day-history';
import { useAppUser } from '../../data/queries/use-app-user';
import {
  describeMyDayFailure,
  isStaleMyDayWrite,
  reopenMyDayItem,
  resumeMyDaySeries,
} from '../../data/writers/my-day-writers';
import { formatInstant } from '../../lib/timezone';
import { MY_DAY_PATH } from './my-day-route';
import type { MyDayHistoryRow } from '../../data/database.types';
import './my-day.css';

/**
 * MY DAY HISTORY — WD-1 (0030, D-050).
 *
 * **A READ OF THE APPEND-ONLY EVENT LOG, SHOWN AS IT CAME.** The last 100
 * events, newest first — the bound and the order are the database's, and the
 * screen neither sorts nor filters. This is what makes a recurring
 * follow-up's past tellable: the active row has advanced, but every occurrence
 * it completed, skipped or missed is still here under its own date.
 *
 * **EVERY INSTANT RENDERS IN THE CANONICAL CREWLINK TIMEZONE** (INV-T17),
 * read from the profile — the same zone the events were bucketed and authored
 * against. No device zone touches a stored instant.
 *
 * **THE DETAIL IS A QUOTE, NOT A COMPUTATION.** Each line says what the event
 * recorded at the time — a skipped occurrence's own date, the day a reschedule
 * moved to — never what this screen re-derives from the current row.
 *
 * **AND THE NAME IS A QUOTE TOO** (M1). An entry is titled from the title the
 * EVENT snapshotted, so renaming a follow-up today cannot rewrite what an
 * entry from March appears to have been about. The current row's title is a
 * fallback for one case only: an event written before the snapshot existed.
 *
 * **HISTORY IS ALSO WHERE THINGS COME BACK FROM** (M2). A finished or
 * dismissed follow-up offers Reopen; an ended repeat offers Resume series —
 * two different acts, named differently, because they are.
 */
export function MyDayHistoryScreen() {
  const { status, events, error, reload } = useMyDayHistory();
  const { profile } = useAppUser();
  const timezone = profile?.timezone ?? null;
  const [notice, setNotice] = useState<string | null>(null);
  /** The event whose recovery is in flight — one at a time, and it says which. */
  const [busy, setBusy] = useState<string | null>(null);

  const recovery = recoverableEvents(events);
  const reversed = reversedEventIds(events);

  async function recover(event: MyDayHistoryRow, kind: RecoveryKind): Promise<void> {
    const client = getSupabaseClient();
    if (!client) return;
    setNotice(null);
    setBusy(event.event_id);
    try {
      // **THE STALE TOKEN IS THE STATE THIS PAGE WAS SHOWING.** A History
      // screen can sit open for a long time; if the item moved on somewhere
      // else, the server refuses instead of overwriting a newer mutation.
      // **TWO VERBS, NEVER ONE.** A one-time follow-up comes back through the
      // authoritative `reopen` writer on its original schedule; an ended
      // repeat comes back through `resume_my_day_series`, which keeps the
      // authored rule and anchor and picks up the next occurrence still ahead.
      if (kind === 'resume_series') {
        await resumeMyDaySeries(client, event.item_id, event.item_state);
      } else {
        await reopenMyDayItem(client, event.item_id, event.item_state);
      }
      await reload();
    } catch (caught) {
      setNotice(describeMyDayFailure(caught));
      // A stale refusal means this page is out of date, so it re-reads rather
      // than leaving him looking at an offer the server has already declined.
      if (isStaleMyDayWrite(caught)) await reload();
    } finally {
      setBusy(null);
    }
  }

  return (
    <main className="cl-screen my-day-screen">
      <p className="my-day-back project-home-back">
        <Link className="cl-link" to={MY_DAY_PATH}>
          ‹ My Day
        </Link>
      </p>

      <h1 className="cl-screen-title">History</h1>
      <p className="my-day-lead">The last 100 things that happened to your follow-ups.</p>

      {notice ? (
        <p className="cl-error" role="alert">
          {notice}
        </p>
      ) : null}

      {status === 'loading' ? <p className="my-day-lead">Loading…</p> : null}

      {status === 'failed' ? (
        <p className="cl-error" role="alert">
          {error ?? 'Crewlink could not load your history.'}
        </p>
      ) : null}

      {status === 'ready' ? (
        events.length === 0 ? (
          <p className="my-day-empty">Nothing has happened yet.</p>
        ) : (
          <ul className="my-day-list my-day-history">
            {events.map((event) => {
              const kind = recovery.get(event.event_id) ?? null;
              const isReversed = reversed.has(event.event_id);
              return (
                <li
                  key={event.event_id}
                  className={isReversed ? 'my-day-item my-day-item--reversed' : 'my-day-item'}
                >
                  <div className="my-day-item-main">
                    <span className="my-day-item-title">
                      {describeEvent(event)} — {historicalTitle(event)}
                      {/* **REVERSED, NOT REMOVED** (N4). The log is append-only,
                          so an undone completion and the catch-up skips that
                          came with it stay exactly where they were written —
                          marked here so a later re-completion of the same
                          occurrence does not read as two independent facts. */}
                      {isReversed ? <span className="my-day-flag">Undone</span> : null}
                    </span>
                    {describeEventDetail(event) ? (
                      <span className="my-day-item-description">{describeEventDetail(event)}</span>
                    ) : null}
                    <span className="my-day-item-when">
                      {timezone === null
                        ? event.occurred_at
                        : formatInstant(event.occurred_at, timezone, {
                            dateStyle: 'medium',
                            timeStyle: 'short',
                          }) || event.occurred_at}
                    </span>
                  </div>
                  {kind === null ? null : (
                    <span className="my-day-item-actions">
                      <button
                        type="button"
                        className="cl-button cl-button--quiet"
                        disabled={busy !== null}
                        onClick={() => void recover(event, kind)}
                      >
                        {busy === event.event_id ? '…' : describeRecovery(kind)}
                      </button>
                    </span>
                  )}
                </li>
              );
            })}
          </ul>
        )
      ) : null}
    </main>
  );
}

/** A string the event's own detail recorded, or null. **Never re-derived.** */
function detailText(event: MyDayHistoryRow, key: string): string | null {
  const value = event.detail[key];
  return typeof value === 'string' && value !== '' ? value : null;
}

/** A `seq` the event's own detail recorded, or null. Integers only. */
function detailSeq(event: MyDayHistoryRow, key: string): number | null {
  const value = event.detail[key];
  return typeof value === 'number' && Number.isInteger(value) ? value : null;
}

/**
 * **THE TITLE THE EVENT SNAPSHOTTED — the historical truth** (M1).
 *
 * Every writer records the title as it stood when it wrote, so an entry names
 * itself. Renaming *"Call plumber"* to *"Call roofer"* today changes what the
 * ACTIVE row says and nothing about what an entry from last month says.
 *
 * The current row's title is the fallback for exactly one case: an event
 * written before the snapshot existed. It is never preferred, because
 * preferring it is the defect.
 */
export function historicalTitle(event: MyDayHistoryRow): string {
  return detailText(event, 'title') ?? event.item_title;
}

/** Which recovery an entry offers. Two acts, deliberately not one word. */
export type RecoveryKind = 'reopen' | 'resume_series';

export function describeRecovery(kind: RecoveryKind): string {
  return kind === 'resume_series' ? 'Resume series' : 'Reopen';
}

/**
 * **WHICH ENTRIES CAN BRING SOMETHING BACK, AND BY WHICH VERB** (M2).
 *
 * The offer belongs to the item's NEWEST entry on this page — the one that put
 * it where it is now — so a follow-up completed, reopened and completed again
 * shows one Reopen, not one per historical completion.
 *
 * **AN ENDED REPEAT IS NOT OFFERED A REOPEN.** Reopen restores an item to the
 * state it was in, which for a repeat that ended months ago means waking it on
 * a long-dead occurrence. `resume_series` keeps the authored rule and picks up
 * on the next occurrence still ahead, and the button says so.
 */
export function recoverableEvents(
  events: readonly MyDayHistoryRow[],
): ReadonlyMap<string, RecoveryKind> {
  const offered = new Map<string, RecoveryKind>();
  const seen = new Set<string>();
  // The server's order is newest-first, so the first entry per item IS its
  // newest. Nothing here sorts.
  for (const event of events) {
    if (seen.has(event.item_id)) continue;
    seen.add(event.item_id);
    // An open follow-up is already back; there is nothing to recover.
    if (event.item_state === 'open') continue;
    offered.set(event.event_id, event.recur_rule === null ? 'reopen' : 'resume_series');
  }
  return offered;
}

/**
 * **THE ENTRIES A LATER UNDO TOOK BACK** (N4).
 *
 * `undo_my_day_occurrence` records the contiguous `seq` run it reversed, so
 * History can mark those entries without touching them — the log stays
 * append-only, and the entries stay exactly as they were written.
 *
 * By `seq`, never by timestamp: a whole advance chain shares `occurred_at` to
 * the microsecond because one transaction wrote it, and "same instant" is not
 * the same claim as "reversed by this undo".
 */
export function reversedEventIds(events: readonly MyDayHistoryRow[]): ReadonlySet<string> {
  const reversed = new Set<string>();
  for (const undo of events) {
    if (undo.event_type !== 'reopened') continue;
    const start = detailSeq(undo, 'reversed_seq_start');
    const end = detailSeq(undo, 'reversed_seq_end');
    if (start === null || end === null) continue;
    for (const event of events) {
      if (event.item_id !== undo.item_id) continue;
      if (event.seq >= start && event.seq <= end) reversed.add(event.event_id);
    }
  }
  return reversed;
}

/**
 * The event, as one plain word or two. **The type comes from the database's
 * closed CHECK vocabulary**; a value outside it renders as itself rather than
 * being guessed at.
 */
export function describeEvent(event: MyDayHistoryRow): string {
  switch (event.event_type) {
    case 'created':
      return 'Added';
    case 'edited':
      return 'Edited';
    case 'rescheduled':
      return 'Rescheduled';
    case 'completed':
      return 'Done';
    case 'reopened':
      // **THREE THINGS REOPEN A ROW, AND THEY ARE NOT THE SAME EVENT TO READ**
      // (M2 / N4): picking an ended repeat back up, taking back an advance,
      // and plainly undoing a Done. The detail says which; nothing is guessed.
      if (detailText(event, 'reason') === 'series_resumed') return 'Series resumed';
      return detailText(event, 'undo_of') === null ? 'Reopened' : 'Undone';
    case 'dismissed':
      return 'Dismissed';
    case 'snoozed':
      return 'Snoozed';
    case 'unsnoozed':
      return 'Snooze ended';
    case 'occurrence_completed':
      return 'Done';
    case 'occurrence_skipped':
      // **THE REASON THE EVENT RECORDED, not a guess**: 'dismissed' was a
      // deliberate Skip; 'catch_up' is a slot that passed while nothing was
      // ticked — and the record says so honestly rather than blending them.
      return detailText(event, 'reason') === 'catch_up' ? 'Missed' : 'Skipped';
    case 'series_ended':
      return detailText(event, 'cause') === 'exhausted' ? 'Series finished' : 'Series ended';
    case 'capture_linked':
      return 'Capture linked';
    case 'capture_unlinked':
      return 'Capture unlinked';
    default:
      return event.event_type;
  }
}

/** The one fact worth a second line, quoted from the event's own snapshot. */
export function describeEventDetail(event: MyDayHistoryRow): string | null {
  switch (event.event_type) {
    case 'edited': {
      // **THE RENAME, IN BOTH ITS NAMES** (M1). `title_change` is the diff;
      // `title` is this entry's own historical name and is used for the
      // heading, so the two never compete for one key.
      const change = event.detail.title_change;
      if (change === null || typeof change !== 'object') return null;
      const prior = (change as { prior?: unknown }).prior;
      return typeof prior === 'string' && prior !== '' ? `It was called “${prior}”.` : null;
    }
    case 'reopened': {
      if (detailText(event, 'reason') === 'series_resumed') {
        const resumed = detailText(event, 'resumed_to');
        return resumed === null ? null : `Picked back up on ${resumed}.`;
      }
      const restored = detailText(event, 'restored_occurrence');
      return restored === null ? null : `It put the ${restored} one back.`;
    }
    case 'occurrence_completed':
    case 'occurrence_skipped': {
      const date = detailText(event, 'occurrence_date');
      return date === null ? null : `The ${date} one.`;
    }
    case 'series_ended': {
      const last = detailText(event, 'last_occurrence');
      return detailText(event, 'cause') === 'exhausted' && last !== null
        ? `Its last day was ${last}.`
        : null;
    }
    default:
      return null;
  }
}
